import { collection, query, where, orderBy, onSnapshot, doc, getDoc, addDoc, updateDoc, deleteDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase-init';
import { justDate, toDate, addDays, isWeekend } from '../utils/date-utils';
import { isHoliday, getHolidaySet } from '../utils/holiday-calculator';
import { estimateSoldOutDate } from '../utils/sold-out-algorithm';

class JobService {
  constructor() {
    this.jobs = [];
    this.listeners = [];
    this.unsubscribe = null;
  }

  // Start real-time listener
  //
  // Bounded to the last 18 months of job creation — an unbounded live
  // listener here re-reads EVERY job this company has ever done on every
  // job change anywhere, for every open session; `jobs` only grows and is
  // this app's single biggest Firestore cost driver. 18 months keeps every
  // feature that reads from this listener working: Calendar (current/
  // upcoming schedule), CRM Dashboard's 6-month trend, and the Calendar
  // utilization KPI's "same month last year" comparison (needs up to 12
  // months back) all fit comfortably inside the window with room to spare.
  //
  // Tradeoff: Jobs Database's search/filter only searches jobs already in
  // this listener's in-memory list, so a job created more than 18 months
  // ago will no longer appear there. If full historical search is ever
  // needed again, that should be a separate one-time getDocs() query in
  // JobsDatabasePage, not a widening of this always-on listener.
  startListener(callback) {
    if (this.unsubscribe) {
      console.warn('JobService listener already active');
      return;
    }

    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 18);

    const q = query(
      collection(db, 'jobs'),
      where('createdAt', '>=', Timestamp.fromDate(cutoff)),
      orderBy('createdAt', 'desc')
    );

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      this.jobs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore Timestamps to Dates
        soldDate: toDate(doc.data().soldDate),
        scheduledDate: toDate(doc.data().scheduledDate),
        createdAt: toDate(doc.data().createdAt),
        updatedAt: toDate(doc.data().updatedAt),
        onHoldDate: toDate(doc.data().onHoldDate),
        workDays: (doc.data().workDays || []).map(wd => ({
          ...wd,
          date: toDate(wd.date),
          createdAt: toDate(wd.createdAt)
        }))
      }));

      
      // Notify all listeners
      this.listeners.forEach(cb => cb(this.jobs));
      if (callback) callback(this.jobs);
    }, (error) => {
      console.error('❌ JobService listener error:', error);
    });
  }

  // Stop listener
  stopListener() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  // Subscribe to job updates
  subscribe(callback) {
    this.listeners.push(callback);
    // Immediately call with current data
    callback(this.jobs);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  // Get all jobs
  getJobs() {
    return this.jobs;
  }

  // Get job by ID
  async getJob(id) {
    const docRef = doc(db, 'jobs', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        soldDate: toDate(data.soldDate),
        scheduledDate: toDate(data.scheduledDate),
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        onHoldDate: toDate(data.onHoldDate),
        workDays: (data.workDays || []).map(wd => ({
          ...wd,
          date: toDate(wd.date),
          createdAt: toDate(wd.createdAt)
        }))
      };
    }
    
    return null;
  }

  // Add new job
  async addJob(jobData) {
    const now = new Date();
    
    const data = {
      ...jobData,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
      soldDate: jobData.soldDate ? Timestamp.fromDate(jobData.soldDate) : Timestamp.fromDate(now),
      scheduledDate: jobData.scheduledDate ? Timestamp.fromDate(jobData.scheduledDate) : null,
      statusHistory: jobData.statusHistory || [],
      images: jobData.images || [],
      contracts: jobData.contracts || [],
      workDays: jobData.workDays || [],
      onHoldReason: jobData.onHoldReason || null,
      onHoldDate: jobData.onHoldDate ? Timestamp.fromDate(jobData.onHoldDate) : null,
      onHoldBy: jobData.onHoldBy || null
    };

    const docRef = await addDoc(collection(db, 'jobs'), data);
    
    return docRef.id;
  }

  // Update job
  async updateJob(id, updates) {
    const docRef = doc(db, 'jobs', id);
    
    // Get current job first to check status
    const currentJob = await this.getJob(id);
    
    const data = {
      ...updates,
      updatedAt: Timestamp.fromDate(new Date())
    };

    // Convert dates to Timestamps
    if (updates.soldDate) {
      data.soldDate = Timestamp.fromDate(updates.soldDate);
    }
    
    // CRITICAL: Don't clear scheduledDate if status is changing to "on_hold"
    if (updates.scheduledDate !== undefined && updates.status !== 'on_hold') {
      data.scheduledDate = updates.scheduledDate ? Timestamp.fromDate(updates.scheduledDate) : null;
    }
    
    if (updates.onHoldDate) {
      data.onHoldDate = Timestamp.fromDate(updates.onHoldDate);
    }
    if (updates.workDays) {
      data.workDays = updates.workDays.map(wd => ({
        ...wd,
        date: Timestamp.fromDate(wd.date),
        createdAt: Timestamp.fromDate(wd.createdAt || new Date())
      }));
    }

    // Handle "On Hold" status change
    if (updates.status === 'on_hold') {
      // DON'T clear scheduledDate - keep it for history
      // Mark first workDay as "on_hold" instead of clearing
      if (currentJob && currentJob.workDays && currentJob.workDays.length > 0) {
        data.workDays = currentJob.workDays.map((wd, index) => {
          if (index === 0) {
            // Mark first day as "on_hold"
            return {
              ...wd,
              date: Timestamp.fromDate(wd.date),
              status: 'on_hold',
              note: updates.onHoldReason || 'Job placed on hold',
              createdAt: Timestamp.fromDate(wd.createdAt || new Date())
            };
          } else {
            // Mark remaining days as "cancelled"
            return {
              ...wd,
              date: Timestamp.fromDate(wd.date),
              status: 'cancelled',
              createdAt: Timestamp.fromDate(wd.createdAt || new Date())
            };
          }
        });
      }
      // Keep the scheduledDate - DON'T clear it
      data.onHoldDate = Timestamp.fromDate(new Date());
      data.onHoldBy = updates.onHoldBy || null;
      data.onHoldReason = updates.onHoldReason || null;
    }

    // Clear on hold fields if changing away from "On Hold"
    if (currentJob && currentJob.status === 'on_hold' && updates.status && updates.status !== 'on_hold') {
      data.onHoldReason = null;
      data.onHoldDate = null;
      data.onHoldBy = null;
    }

    await updateDoc(docRef, data);
  }

  // Delete job
  async deleteJob(id) {
    // Also delete linked cutlist job if exists
    try {
      const docRef = doc(db, 'jobs', id);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const cutlistJobId = snap.data().cutlistJobId;
        if (cutlistJobId) {
          await deleteDoc(doc(db, 'cutlistJobs', cutlistJobId));
        }
      }
      await deleteDoc(docRef);
    } catch(e) {
      console.error('Error deleting job:', e);
      throw e;
    }
  }

  // Schedule job
  async scheduleJob(id, date, crew, durationDays) {
    const normalizedDate = this.normalizeStartDate(date);
    const workDays = this.buildWorkDays(normalizedDate, durationDays);

    await this.updateJob(id, {
      scheduledDate: normalizedDate,
      assignedCrew: crew,
      durationDays: workDays.length,
      workDays: workDays,
      status: 'scheduled'
    });
  }

  // Normalize start date (skip weekends and holidays)
  normalizeStartDate(proposedDate) {
    let d = justDate(proposedDate);
    
    while (isWeekend(d) || isHoliday(d)) {
      d = addDays(d, 1);
    }
    
    return d;
  }

  // Build work days array (skip weekends and holidays)
  buildWorkDays(startDate, duration) {
    const workDays = [];
    let cursor = justDate(startDate);
    
    while (workDays.length < duration) {
      if (!isWeekend(cursor) && !isHoliday(cursor)) {
        workDays.push({
          id: cursor.toISOString().slice(0, 10),
          date: cursor,
          status: 'planned',
          note: null,
          createdAt: new Date()
        });
      }
      cursor = addDays(cursor, 1);
    }
    
    return workDays;
  }

  // Get jobs for a specific date
  getJobsForDate(date) {
    const targetDate = justDate(date);
    const targetISO = targetDate.toISOString().slice(0, 10);
    
    return this.jobs.filter(job => {
      // DON'T skip "On Hold" jobs - show them in orange
      // if (job.status === 'on_hold') return false;
      
      // Check explicit workDays first
      if (job.workDays && job.workDays.length > 0) {
        return job.workDays.some(wd => {
          const wdISO = justDate(wd.date).toISOString().slice(0, 10);
          return wdISO === targetISO && wd.status !== 'noWork' && wd.status !== 'cancelled';
        });
      }
      
      // Fallback to legacy scheduledDate + duration
      if (job.scheduledDate && job.status !== 'on_hold') {
        const dates = this.getJobCalendarDates(job);
        return dates.some(d => {
          const dISO = justDate(d).toISOString().slice(0, 10);
          return dISO === targetISO;
        });
      }
      
      return false;
    });
  }

  // Get calendar dates for a job (supports both new and legacy)
  getJobCalendarDates(job) {
    // DON'T skip "On Hold" jobs - show them with workDay status
    // if (job.status === 'on_hold') return [];
    
    // New explicit workDays model
    if (job.workDays && job.workDays.length > 0) {
      return job.workDays
        .filter(wd => wd.status !== 'noWork' && wd.status !== 'cancelled')
        .map(wd => justDate(wd.date))
        .sort((a, b) => a - b);
    }
    
    // Legacy: scheduledDate + duration
    if (!job.scheduledDate) return [];
    
    const year = new Date(job.scheduledDate).getFullYear();
    const holidays = getHolidaySet(year);
    const duration = Math.max(job.durationDays || 1, 1);
    
    const dates = [];
    let cursor = justDate(job.scheduledDate);
    
    while (dates.length < duration) {
      const cursorISO = cursor.toISOString().slice(0, 10);
      if (!isWeekend(cursor) && !holidays.has(cursorISO)) {
        dates.push(new Date(cursor));
      }
      cursor = addDays(cursor, 1);
    }
    
    return dates;
  }

  // Get furthest scheduled date
  getFurthestScheduledDate() {
    let furthest = null;
    
    this.jobs.forEach(job => {
      // Skip "On Hold" jobs for furthest date calculation
      if (job.status === 'on_hold') return;
      
      const dates = this.getJobCalendarDates(job);
      dates.forEach(date => {
        if (!furthest || date > furthest) {
          furthest = date;
        }
      });
    });
    
    return furthest;
  }

  // Get sold-out-through estimate
  getSoldOutThroughDate() {
    return estimateSoldOutDate(this.jobs);
  }

  // Get unscheduled jobs (includes "On Hold")
  getUnscheduledJobs() {
    return this.jobs.filter(job => 
      !job.scheduledDate && 
      !job.isRepair && 
      job.status !== 'completed' && 
      job.status !== 'cancelled'
    ).sort((a, b) => {
      const dateA = a.soldDate || new Date(0);
      const dateB = b.soldDate || new Date(0);
      return dateA - dateB;
    });
  }

  // Get "On Hold" jobs
  getOnHoldJobs() {
    return this.jobs.filter(job => job.status === 'on_hold')
      .sort((a, b) => {
        const dateA = a.onHoldDate || new Date(0);
        const dateB = b.onHoldDate || new Date(0);
        return dateB - dateA; // Most recent first
      });
  }

  // Get jobs by status
  getJobsByStatus(status) {
    return this.jobs.filter(job => 
      (job.status || '').toLowerCase() === status.toLowerCase()
    );
  }
}

// Export singleton instance
export const jobService = new JobService();
export default jobService;